import { parseISO, parse, formatDistanceToNow } from 'date-fns';
import { formatInstant, formatUtc } from './dateTime';
import { parseUtcTimestamp } from './utcDateTime';

/** Legacy noncalendar display inputs: offset-less ISO and literal dates use device-local fields.
 * Undefined retains the legacy current-clock default. Null and malformed values remain invalid.
 * This adapter never serializes dates or reinterprets retained storage.
 */
export const legacyLocalDate = (value?: Date | string | number | null): Date => {
  if (value === undefined) return new Date();
  if (value === null) return new Date(NaN);
  if (value instanceof Date) return new Date(value);
  if (typeof value === 'number') return new Date(value);
  const iso = parseISO(value);
  if (Number.isFinite(+iso)) return iso;
  // Existing file timestamps also accept RFC/GMT labels. Require the explicit
  // RFC shape rather than allowing browser-dependent arbitrary date parsing.
  if (/^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{1,2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} (?:GMT|[+-]\d{4})$/.test(value)) return new Date(value);
  // Preserve the historical US date display input, without interpreting it UTC.
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) return parse(value, 'M/d/yyyy', new Date());
  return new Date(NaN);
};

/** Use only on proven UTC timestamp fields previously consumed with utc parsing. */
export const legacyUtcDate = (value?: Date | string | number | null): Date => {
  if (value === undefined) return new Date();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string' && /(?:GMT|[+-]\d{4})$/.test(value)) return legacyLocalDate(value);
  if (typeof value === 'string' && !Number.isFinite(+parseISO(value))) return new Date(NaN);
  return parseUtcTimestamp(value) ?? new Date(NaN);
};

export const legacyLocalLabel = (value: Date | string | number | null | undefined, pattern: string, utcInput = false): string => {
  const date = utcInput ? legacyUtcDate(value) : legacyLocalDate(value);
  return Number.isFinite(+date) ? formatInstant(date, pattern) : 'Invalid date';
};
export const legacyUtcLabel = (value: Date | string | number | null | undefined, pattern: string): string => {
  const date = legacyUtcDate(value);
  return Number.isFinite(+date) ? formatUtc(date, pattern) : 'Invalid date';
};
export const relativeInstant = (value: Date | string | number | null | undefined, utcInput = false): string => {
  const date = utcInput ? legacyUtcDate(value) : legacyLocalDate(value);
  return Number.isFinite(+date) ? formatDistanceToNow(date, { addSuffix: true }) : 'Invalid date';
};
